import { MembershipItemId } from '../../commonv3/types/membershipPlans';
import { MembershipTokenIndexView } from './membershipTokens';
import moment, { Moment } from 'moment-timezone';
import { MembershipTokenChainId } from '../../commonv3/types/membership_tokens';
import { ISODate } from '../../commonv3/types/dates';
import { SubscriptionSuspension, SubscriptionId, SubscriptionSuspensionId } from './subscriptionSuspension';
import { SubscriptionIndexView } from './subscriptions';

export type MembershipSummary = {
  chain_id: MembershipTokenChainId;
  parent_chain_id: MembershipTokenChainId;
  membership_item_id: MembershipItemId;
  plan_name: string;
  subscribed: boolean;
  continued_since: Moment;
  next_payment_date: Moment;
  expires_at: Moment;
  strict_expires_at: Moment;
  plan_item_duration_month: number;
  plan_item_price: number;
  plan_item_price_with_tax: number;
  suspended: boolean;
  future_suspension: boolean;
  subscription: SubscriptionIndexView | null;
  subscription_id: SubscriptionId | null;
  subscription_code: string | null;
  subscription_suspension_id: SubscriptionSuspensionId | null;
  subscription_suspension: SubscriptionSuspension | null;
};

export function isExpired(summary) {
  return !summary.suspended && summary.expires_at.isSameOrBefore(moment().startOf('day'));
}

export function getKey(summary) {
  return `${summary.plan_name}-${summary.continued_since}`;
}

function nullSafeMin(x: ISODate | Moment | null, y: ISODate | Moment | null): Moment | null {
  if (!x && !y) {
    return null;
  }
  if (!y) {
    return moment(x);
  }
  if (!x) {
    return moment(y);
  }
  return moment(x).isBefore(moment(y)) ? moment(x) : moment(y);
}

function nullSafeMax(x: ISODate | Moment | null, y: ISODate | Moment | null): Moment | null {
  if (!x && !y) {
    return null;
  }
  if (!y) {
    return moment(x);
  }
  if (!x) {
    return moment(y);
  }
  return moment(x).isBefore(moment(y)) ? moment(y) : moment(x);
}

function nullUnlessFuture(date) {
  if (moment().isAfter(date)) {
    return null;
  }
  return date;
}

function getTokenPaymentDate(token: MembershipTokenIndexView): Moment | null {
  return nullSafeMin(nullUnlessFuture(token.payment_due_date), nullUnlessFuture(token.subscription_renewal_date));
}

function getStrictExpiresAt(token: MembershipTokenIndexView): Moment | null {
  if (token.future) {
    return null;
  }
  return moment(token.expires_at);
}

function getInitialSummary(token: MembershipTokenIndexView): MembershipSummary {
  return {
    chain_id: token.chain_id,
    parent_chain_id: token.parent_chain_id,
    membership_item_id: token.membership_item_id,
    plan_name: token.plan_item_name,
    subscribed: token.subscribed,
    continued_since: moment(token.continued_since),
    expires_at: moment(token.expires_at),
    strict_expires_at: getStrictExpiresAt(token),
    next_payment_date: getTokenPaymentDate(token),
    plan_item_duration_month: token.plan_item_duration_month,
    plan_item_price: token.plan_item_price,
    plan_item_price_with_tax: token.plan_item_price_with_tax,
    suspended: token.suspended,
    future_suspension: token.future_suspension,
    subscription: token.subscription,
    subscription_id: token.subscription_id,
    subscription_code: token.subscription_code,
    subscription_suspension_id: token.subscription_suspension_id,
    subscription_suspension: token.subscription_suspension,
  };
}

function reduceSummary(currentSummary: MembershipSummary, token: MembershipTokenIndexView): MembershipSummary {
  const token_payment_date = getTokenPaymentDate(token);
  const next_payment_date = nullSafeMin(currentSummary.next_payment_date, token_payment_date);
  const expires_at = nullSafeMax(currentSummary.expires_at, token.expires_at);
  const strict_expires_at = nullSafeMax(currentSummary.strict_expires_at, getStrictExpiresAt(token));
  return {
    ...currentSummary,
    expires_at,
    strict_expires_at,
    next_payment_date,
  };
}

export function createSummaries(tokens: MembershipTokenIndexView[]): MembershipSummary[] {
  const summaries: MembershipSummary[] = Object.values(
    tokens.reduce((current, token) => {
      const aggKey = token.chain_id;
      const currentSummary = current[aggKey];
      if (!currentSummary) {
        current[aggKey] = getInitialSummary(token);
      } else {
        current[aggKey] = reduceSummary(currentSummary, token);
      }
      return current;
    }, {})
  );
  return summaries.sort((x, y) => moment(x.continued_since).diff(moment(y.continued_since)));
}
